import { tx as translateInterfaceText } from '@/utils/i18n';
import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
// =====================================================
// Tipos da API Backend (contracts)
// =====================================================

export type AccessType = 'ADMIN' | 'MEMBER';

export type BackendPermission = string;

export interface FolderAccessEntry {
  folder_id: string;
  level: 'view' | 'edit';
}

export interface BackendMemberLink {
  id: string;
  member_user_id: string;
  name: string;
  email: string;
  avatar?: string;
  permissions: BackendPermission[];
  access_type: AccessType;
  folder_access: FolderAccessEntry[];
  status: 'ACTIVE' | 'INACTIVE';
  created_at: string;
}

export interface CreateMemberPayload {
  name: string;
  email: string;
  password: string;
  permissions: BackendPermission[];
  accessType: AccessType;
  folderAccess?: FolderAccessEntry[];
}

export interface UpdateMemberPayload {
  name?: string;
  email?: string;
  password?: string;
  permissions?: BackendPermission[];
  accessType?: AccessType;
  folderAccess?: FolderAccessEntry[];
}

// =====================================================
// Feature Catalog
// =====================================================

export interface FeatureItem {
  key: string;
  label: string;
  group: string;
  levels: ('view' | 'edit')[];
  icon?: string;
}

export const FEATURES: FeatureItem[] = [
  { key: 'videos', label: 'Meus Vídeos', group: 'videos', levels: ['view', 'edit'], icon: 'Video' },
  { key: 'personalizacoes', label: 'Personalizar', group: 'videos', levels: ['view', 'edit'], icon: 'Palette' },
  { key: 'compartilhar', label: 'Compartilhar', group: 'videos', levels: ['view', 'edit'], icon: 'Share2' },
  { key: 'vsleads', label: 'Captura de Leads', group: 'conversao', levels: ['view', 'edit'], icon: 'Target' },
  { key: 'metricas', label: 'Métricas', group: 'analises', levels: ['view', 'edit'], icon: 'BarChart3' },
  { key: 'tempo_real', label: 'Tempo Real', group: 'analises', levels: ['view'], icon: 'Clock' },
  { key: 'rastreamento', label: 'Rastreamento', group: 'analises', levels: ['view', 'edit'], icon: 'MousePointerClick' },
  { key: 'comparar_vsl', label: 'Comparar VSL', group: 'analises', levels: ['view', 'edit'], icon: 'GitCompare' },
  { key: 'ab_test', label: 'Teste A/B', group: 'otimizacao', levels: ['view', 'edit'], icon: 'FlaskConical' },
  { key: 'hook_track', label: 'Teste de hook', group: 'otimizacao', levels: ['view', 'edit'], icon: 'Anchor' },
  { key: 'cloaker', label: 'Cloaker', group: 'protecao', levels: ['view', 'edit'], icon: 'Shield' },
  { key: 'pixel', label: 'Pixel', group: 'protecao', levels: ['view', 'edit'], icon: 'Crosshair' },
  { key: 'dominios', label: 'Domínios', group: 'protecao', levels: ['view', 'edit'], icon: 'Globe' },
  { key: 'sites', label: 'Criar Sites', group: 'ferramentas', levels: ['view'], icon: 'LayoutGrid' },
  { key: 'quiz', label: 'Criar Quiz', group: 'ferramentas', levels: ['view'], icon: 'HelpCircle' },
  { key: 'academy', label: 'Academy', group: 'aprendizado', levels: ['view'], icon: 'GraduationCap' },
  { key: 'parceiros', label: 'Parceiros', group: 'aprendizado', levels: ['view'], icon: 'Handshake' },
  { key: 'webinarios', label: 'Webinários', group: 'extras', levels: ['view', 'edit'], icon: 'MonitorPlay' },
  { key: 'playlists', label: 'Playlists', group: 'extras', levels: ['view', 'edit'], icon: 'ListVideo' },
  { key: 'master_class', label: 'Master Class', group: 'extras', levels: ['view', 'edit'], icon: 'Presentation' },
  { key: 'indique_ganhe', label: 'Indique e Ganhe', group: 'extras', levels: ['view'], icon: 'Gift' },
  { key: 'documentacao', label: 'Documentação', group: 'extras', levels: ['view'], icon: 'FileText' },
  { key: 'financeiro', label: 'Financeiro', group: 'conta', levels: ['view', 'edit'], icon: 'DollarSign' },
  { key: 'membros', label: 'Membros', group: 'conta', levels: ['view', 'edit'], icon: 'Users' },
  { key: 'perfil', label: 'Perfil', group: 'conta', levels: ['view', 'edit'], icon: 'User' },
  { key: 'seguranca', label: 'Segurança', group: 'conta', levels: ['view', 'edit'], icon: 'Lock' },
];

export const FEATURE_GROUP_LABELS: Record<string, string> = {
  get videos() { return translateInterfaceText("Vídeos"); },
  get conversao() { return translateInterfaceText("Conversão"); },
  get analises() { return translateInterfaceText("Análises"); },
  get otimizacao() { return translateInterfaceText("Otimização"); },
  get protecao() { return translateInterfaceText("Proteção"); },
  ferramentas: 'Ferramentas',
  aprendizado: 'Aprendizado',
  extras: 'Extras',
  conta: 'Conta',
};

// =====================================================
// UI Types
// =====================================================

export type MemberRole = 'visualizador' | 'editor' | 'administrador';
export type MemberStatus = 'ativo' | 'expirado' | 'pendente';

export interface Member {
  id: string;
  owner_id: string;
  name: string;
  email: string;
  role: MemberRole;
  status: MemberStatus;
  access_type: AccessType;
  folder_access: FolderAccessEntry[];
  permissions: BackendPermission[];
  invited_at: string;
  accepted_at?: string;
}

export interface AddMemberFormData {
  name: string;
  email: string;
  password: string;
  role: MemberRole;
  accessType: AccessType;
  permissions: BackendPermission[];
  folderAccess: FolderAccessEntry[];
}

export interface EditMemberFormData {
  role?: MemberRole;
  name?: string;
  accessType?: AccessType;
  permissions?: BackendPermission[];
  folderAccess?: FolderAccessEntry[];
}

// =====================================================
// Mapeamento
// =====================================================

export const MEMBER_FIXED_PERMISSIONS: BackendPermission[] = [
  'feature:videos:view',
  'feature:videos:edit',
  'feature:personalizacoes:view',
  'feature:personalizacoes:edit',
  'feature:perfil:view',
];

export function featurePerm(key: string, level: 'view' | 'edit') {
  return `feature:${key}:${level}`;
}

export function hasPermission(permissions: BackendPermission[], required: string) {
  if (!required.startsWith('feature:')) return false;
  const [, key, level] = required.split(':');
  if (level === 'edit') return permissions.includes(featurePerm(key, 'edit'));
  return permissions.includes(featurePerm(key, 'view')) || permissions.includes(featurePerm(key, 'edit'));
}

export function mapBackendMemberToUI(link: BackendMemberLink, ownerId: string): Member {
  let status: MemberStatus = 'ativo';
  if (link.status === 'INACTIVE') status = 'expirado';

  const isAdmin = link.access_type === 'ADMIN';
  let role: MemberRole = 'visualizador';
  if (isAdmin) role = 'administrador';
  else if (link.permissions.some((p) => p.includes(':edit'))) role = 'editor';

  return {
    id: link.id,
    owner_id: ownerId,
    name: link.name,
    email: link.email,
    role,
    status,
    access_type: link.access_type,
    folder_access: link.folder_access || [],
    permissions: link.permissions,
    invited_at: link.created_at
      ? new Date(link.created_at).toLocaleDateString(getInterfaceLanguage())
      : '',
  };
}

export function mapFormDataToPayload(data: AddMemberFormData): CreateMemberPayload {
  return {
    name: data.name,
    email: data.email,
    password: data.password,
    permissions: data.accessType === 'ADMIN' ? [] : data.permissions,
    accessType: data.accessType,
    folderAccess: data.accessType === 'ADMIN' ? [] : data.folderAccess,
  };
}
